import { Button, Group, useMantineTheme } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { useContext, useEffect, useState } from "react";
import {
  CategoryOptionType,
  CreateProductGroupInput,
  useCreateProductGroupMutation,
  useGetCategoryOptionManyLazyQuery,
  useUpdateProductGroupMutation,
} from "src/generated/graphql";
import { object, string } from "yup";
import { useModals } from "@mantine/modals";
import TextInput from "src/components/TextInput/TextInput";
import Textarea from "src/components/Textarea/Textarea";
import { memo } from "react";
import FormCard from "src/components/FormCard";
import { HeightCard, MESSAGES, ROUTES } from "src/utils/constants";
import ImageUpload from "src/components/ImageUpload";
import ActionCard from "src/components/ActionCard";
import { GetProductGroup } from "src/api/graphql/queries/Product";
import { ProductContext } from "src/api/context/ProductContext";
import { AppContext } from "src/api/AppContext";
import { useNavigate } from "react-router-dom";
import Select, { ISelect } from "src/components/Select";

const productGroupInitialValues = {
  name: "",
  description: "",
  revenueCategory: "",
};

const productGroupFormSchema = object().shape({
  name: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "Name should must be least 2 letters")
    .max(30, "Name must not be greater than 30 letters")
    .required("Name is required"),
  description: string().max(200).notRequired(),
  revenueCategory: string().required("Revenue Category is required"),
});

export interface IFormProps
  extends Omit<CreateProductGroupInput, "revenueCategory"> {
  revenueCategory: string;
}

type IProps = {
  refetch?: () => void;
  isAllowWrite: boolean;
};

function ProductGroupForm({ refetch, isAllowWrite }: IProps) {
  const { currentLocation, currentOrganization } = useContext(AppContext);
  const { groupId, productGroupData } = useContext(ProductContext);
  const navigate = useNavigate();
  const modals = useModals();
  const theme = useMantineTheme();
  const [isFormActive, { toggle: toggleFormActive }] = useDisclosure(!!groupId);
  const [revenueArr, setrevenueArr] = useState<ISelect[]>([]);

  useEffect(() => {
    productGroupData && resetFormState();
  }, [productGroupData]);

  const [getCategories] = useGetCategoryOptionManyLazyQuery({
    onCompleted(data) {
      let _data = data?.categoryOptionMany;
      let _dataRevenue =
        _data
          ?.filter((e) => e.type === CategoryOptionType.Revenue)
          .map((val) => ({
            value: val.id,
            label: val.name,
          })) || [];
      setrevenueArr(_dataRevenue);
    },
  });

  useEffect(() => {
    if (currentOrganization?.id) {
      getCategories({
        variables: { orgId: currentOrganization?.id || "" },
      });
    }
  }, [currentOrganization, getCategories]);

  const resetFormState = () => {
    if (!productGroupData) return;
    const { name, description, imgUrl, revenueCategory } = productGroupData;
    let _obj = {
      name,
      description,
      imgUrl,
      revenueCategory: revenueCategory?.id!,
      location: { id: currentLocation?.id || "" },
    };
    form.setValues(_obj);
    form.resetDirty(_obj);
  };

  const [updateProductGroup, { loading: updateLoading }] =
    useUpdateProductGroupMutation({
      onCompleted: () => {
        toggleFormActive();
        form.resetDirty();
      },
      refetchQueries: [
        {
          query: GetProductGroup,
          variables: { productGroupOneId: groupId },
        },
      ],
    });

  const [createProductGroup, { loading: createLoading }] =
    useCreateProductGroupMutation({
      onCompleted: (resp) => {
        if (resp.createProductGroup) {
          modals.closeAll();
          navigate(`${ROUTES.PRODUCT}/${resp.createProductGroup.id}`);
        } else {
          refetch ? refetch() : window.location.reload();
        }
      },
    });

  const form = useForm<IFormProps>({
    initialValues: {
      ...productGroupInitialValues,
      location: { id: currentLocation?.id || "" },
    },
    validate: yupResolver(productGroupFormSchema),
  });
  const handleSubmit = async (values: IFormProps) => {
    const { location, revenueCategory, ...rest } = values;
    let _obj = {
      ...rest,
      revenueCategory: { id: revenueCategory },
    };

    {
      groupId
        ? updateProductGroup({
            variables: {
              data: _obj,
              updateProductGroupId: groupId,
            },
          })
        : createProductGroup({
            variables: {
              data: { ...values, revenueCategory: { id: revenueCategory } },
            },
          });
    }
  };

  const handleEdit = () => {
    toggleFormActive();
  };

  const handleClick = () => {
    resetFormState();
    toggleFormActive();
  };

  const isFormUpdated = form.isDirty() && form.isTouched();

  const Form = () => {
    return (
      <>
        <ImageUpload
          onReadSuccess={(b64) => form.setFieldValue("imgUrl", b64)}
          src={form.values.imgUrl!}
          alt="product group Image"
          height="170px"
          maxWidth="100%"
          sx={{ margin: "auto" }}
          disabled={isFormActive}
        />

        <TextInput
          withAsterisk
          label="Name"
          disabled={isFormActive}
          {...form.getInputProps("name")}
        />

        <Select
          withAsterisk
          disabled={isFormActive}
          label="Revenue Category"
          data={revenueArr}
          {...form.getInputProps("revenueCategory")}
        />

        <Textarea
          label="Description"
          disabled={isFormActive}
          {...form.getInputProps("description")}
        />
      </>
    );
  };

  return (
    <FormCard
      title={"Group Info"}
      loading={updateLoading}
      bannerContent={isFormUpdated ? MESSAGES.UNSAVED_CHANGES : ""}
      onCancel={handleClick}
      onEdit={isAllowWrite ? handleEdit : undefined}
      onSave={form.onSubmit((values) => handleSubmit(values))}
      isFormActive={isFormActive}
      hideCard={!groupId}
    >
      {!groupId ? (
        <>
          {Form()}
          <Group position="apart" mt="md" style={{ marginTop: 30 }}>
            <Button
              radius="md"
              variant="outline"
              onClick={() => modals.closeAll()}
              style={{
                flex: 1,
                color: theme.colors.secondaryTextColor[6],
              }}
              sx={{
                backgroundColor: theme.colors.secondary[6],
                "&:hover": {
                  background: theme.colors.secondary[5],
                },
              }}
            >
              Cancel
            </Button>
            <Button
              radius="md"
              type="submit"
              disabled={createLoading || updateLoading}
              style={{
                flex: 1,
              }}
              sx={{
                color: theme.colors.primaryTextColor[6],
                "&:hover": {
                  backgroundColor: theme.colors.primary[4],
                },
              }}
              loading={createLoading || updateLoading}
            >
              Create
            </Button>
          </Group>
        </>
      ) : (
        <ActionCard height={HeightCard.md} title="General">
          {Form()}
        </ActionCard>
      )}
    </FormCard>
  );
}

export default memo(ProductGroupForm);
